/**
 * Utility function to calculate revenue details for a booking
 * This function calculates Eventox revenue, organizer revenue, and Stripe fees
 * based on the booking data and business logic
 * 
 * @param {Object} booking - The booking object containing financial data
 * @returns {Object} Object containing calculated revenue details
 */
export const calcRevenueDetails = (booking) => {
    // Extract and convert financial values to numbers
    const grandTotal = Number(booking.grandTotal || 0);
    const ticketAmount = Number(booking.totalTicketAmount || 0);
    const ticketCount = Number(booking.totalTicketCount || 1);
    const processingFee = Number(booking.processingFee || 0);
    const ticketFee = Number(booking.ticketFee || 0);
    const discount = Number(booking.discountAmount || 0);

    // Determine if fees are collected from organizer
    const feeCollectedFromOrganizer = processingFee === 0 && ticketFee === 0;

    let eventoxRevenue = 0;
    let stripefee = 0;
    let organizerRevenue = 0;


    stripefee = booking.stripeRevenue;
    if (stripefee === null || stripefee === undefined) {
        stripefee = (grandTotal / 100) * 2.7 + 0.3;
    }
    eventoxRevenue = booking.eventoxRevenue;
    if (eventoxRevenue === null || eventoxRevenue === undefined) {
        eventoxRevenue = grandTotal - stripefee - ticketAmount - discount;
    }
    organizerRevenue = booking.organizerRevenue;
    if (organizerRevenue === null || organizerRevenue === undefined) {
        organizerRevenue = ticketAmount - stripefee - eventoxRevenue - discount;
    }
    

    

    // Return calculated revenue details
    return {
        eventoxRevenue: Number(eventoxRevenue) || 0,
        organizerRevenue: Number(organizerRevenue) || 0,
        stripeFee: Number(stripefee) || 0,
        grandTotal: Number(grandTotal) || 0,
        createdAt: Number(booking.createdAt) || 0,
        discount: Number(discount) || 0,
    };
};

/**
 * Calculate summary statistics from an array of bookings
 * 
 * @param {Array} bookings - Array of booking objects
 * @param {Function} revenueCalculator - Function to calculate revenue (defaults to calcRevenueDetails)
 * @returns {Object} Summary statistics object
 */
export const calculateSummaryStats = (bookings, revenueCalculator = calcRevenueDetails) => {
    if (!bookings || bookings.length === 0) {
        return {
            totalBookings: 0,
            totalRevenue: 0,
            totalTickets: 0,
            averageTicketPrice: 0,
            eventoxRevenue: 0,
            organizerRevenue: 0,
            stripeRevenue: 0,
            confirmedBookings: 0,
            cancelledBookings: 0,
            failedBookings: 0,
            pendingBookings: 0,
            totalDiscount: 0,
        };
    }

    const stats = bookings.reduce((acc, booking) => {
        // Only count confirmed bookings for revenue calculations
        if (booking.bookingStatus === 'confirmed') {
           
            acc.totalRevenue += parseFloat(booking.grandTotal || 0);
            // Calculate revenue details for primary bookings only
            if (booking.bookedBy === '0' || booking.bookedBy === 0) {
                const revenue = revenueCalculator(booking);
                acc.eventoxRevenue += revenue.eventoxRevenue;
                acc.organizerRevenue += revenue.organizerRevenue;
                acc.stripeRevenue += revenue.stripeFee;
                acc.totalDiscount += revenue.discount;
                acc.totalTickets += parseInt(booking.totalTicketCount || 0);
                acc.confirmedBookings++;
                acc.totalBookings++;
            }
        }

        // Count by booking status
        if (booking.bookingStatus === 'confirmed') {
            // Already counted above for confirmed bookings
        } else if (booking.bookingStatus === 'cancelled') {
            acc.cancelledBookings++;
        } else if (booking.bookingStatus === 'pending') {
            acc.pendingBookings++;
        }
        else if (booking.bookingStatus?.toLowerCase() === 'failed') {
            acc.failedBookings++;
        }
        
        
        return acc;
    }, {
        totalBookings: 0,
        totalRevenue: 0,
        totalTickets: 0,
        eventoxRevenue: 0,
        organizerRevenue: 0,
        stripeRevenue: 0,
        confirmedBookings: 0,
        cancelledBookings: 0,
        failedBookings: 0,
        pendingBookings: 0,
        totalDiscount: 0,
    });

    // Calculate average ticket price
    stats.averageTicketPrice = stats.totalTickets > 0 ? stats.totalRevenue / stats.totalTickets : 0;

    return stats;
};

/**
 * Format currency values for display
 * 
 * @param {number} amount - The amount to format
 * @param {string} currency - Currency code (default: 'USD')
 * @param {string} locale - Locale for formatting (default: 'en-US')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'USD', locale = 'en-US') => {
    const numAmount = Number(amount) || 0;
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(numAmount);
};

/**
 * Format number with locale-specific formatting
 * 
 * @param {number} number - The number to format
 * @param {string} locale - Locale for formatting (default: 'en-US')
 * @returns {string} Formatted number string
 */
export const formatNumber = (number, locale = 'en-US') => {
    const numValue = Number(number) || 0;
    return numValue.toLocaleString(locale);
}; 